import { messageErreur } from '@imp/shared/message-erreur';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Send, Sparkles } from 'lucide-react';
import { type FormEvent, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { askAssistant, buildContext, type ChatMessage } from '@/modules/assistant/api';

const SUGGESTIONS = [
  'Quelles relances dois-je faire en priorité ?',
  'Résume mes alertes par priorité.',
  'Rédige un email de relance poli pour un devis sans réponse.',
  'Comment créer une facture d’acompte ?',
];

export function AssistantPage() {
  const ctxQ = useQuery({ queryKey: ['assistant-context'], queryFn: buildContext, staleTime: 60_000 });
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  const mut = useMutation({
    mutationFn: (msgs: ChatMessage[]) => askAssistant(msgs, ctxQ.data ?? ''),
    onSuccess: (reply) => {
      setMessages((m) => [...m, { role: 'assistant', content: reply }]);
    },
  });

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, mut.isPending]);

  const send = (text: string) => {
    const t = text.trim();
    if (t === '' || mut.isPending) return;
    const next: ChatMessage[] = [...messages, { role: 'user', content: t }];
    setMessages(next);
    setInput('');
    mut.mutate(next);
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    send(input);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
          <Sparkles className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Assistant</h1>
          <p className="text-muted-foreground">
            Pose tes questions sur tes données, demande un texte ou de l'aide. (IA Mistral, hébergée
            en UE)
          </p>
        </div>
      </div>

      <Card className="flex h-[calc(100dvh-22rem)] min-h-[16rem] flex-col overflow-hidden lg:h-[calc(100vh-12rem)] lg:min-h-[28rem]">
        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-4">
          {messages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
              <Sparkles className="h-8 w-8 text-primary" />
              <p className="max-w-sm text-sm text-muted-foreground">
                Je connais tes devis, chantiers, clients et alertes du moment. Essaie par exemple :
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    disabled={ctxQ.isLoading}
                    onClick={() => {
                      send(s);
                    }}
                    className="rounded-full border bg-card px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-accent disabled:opacity-50"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm ${
                    m.role === 'user'
                      ? 'rounded-br-sm bg-primary text-primary-foreground'
                      : 'rounded-bl-sm bg-muted text-foreground'
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))
          )}
          {mut.isPending ? (
            <div className="flex justify-start">
              <div className="rounded-2xl rounded-bl-sm bg-muted px-4 py-2.5 text-sm text-muted-foreground">
                L'assistant réfléchit…
              </div>
            </div>
          ) : null}
          {mut.isError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
              {messageErreur(mut.error)}
            </div>
          ) : null}
        </div>

        <form onSubmit={onSubmit} className="flex items-end gap-2 border-t bg-card p-3">
          <textarea
            value={input}
            rows={1}
            placeholder={ctxQ.isLoading ? 'Préparation des données…' : 'Écris ta question…'}
            disabled={ctxQ.isLoading}
            onChange={(e) => {
              setInput(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send(input);
              }
            }}
            className="max-h-32 min-h-[3rem] flex-1 resize-none rounded-md border border-input bg-card px-3.5 py-2.5 text-base focus:outline-none lg:min-h-[2.75rem] focus:ring-2 focus:ring-ring disabled:opacity-50"
          />
          <Button type="submit" size="icon" disabled={input.trim() === '' || mut.isPending}>
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </Card>
      <p className="text-center text-xs text-muted-foreground">
        L'assistant peut se tromper — vérifie les montants et coordonnées avant d'agir. Lecture
        seule : il ne modifie rien dans le CRM.
      </p>
    </div>
  );
}
